/**
 * Markdown to HTML renderer for AI responses
 * Converts markdown syntax to properly formatted HTML
 * v10.4.18: Created for consistent markdown rendering across all AI components
 * v11.12.x: Upgraded to use KaTeX for proper equation rendering
 */

import katex from 'katex';

/**
 * Render a LaTeX string using KaTeX. Falls back to plain text on error.
 */
const renderKatex = (math, displayMode = false) => {
  try {
    return katex.renderToString(math, {
      displayMode,
      throwOnError: false,
      output: 'html',
    });
  } catch {
    return `<span style="font-style: italic; font-family: 'Times New Roman', serif;">${math}</span>`;
  }
};

export const markdownToHtml = (text) => {
  if (!text || typeof text !== 'string') return '';

  let html = text;

  // Protect SVG blocks from markdown processing
  const svgBlocks = [];
  html = html.replace(/<svg[\s\S]*?<\/svg>/gi, (match) => {
    const idx = svgBlocks.length;
    svgBlocks.push(match);
    return `{{SVG_BLOCK_${idx}}}`;
  });

  // Protect code blocks from math/markdown processing
  const codeBlocks = [];
  html = html.replace(/```[\s\S]*?```/g, (match) => {
    const idx = codeBlocks.length;
    codeBlocks.push(match);
    return `{{CODE_BLOCK_${idx}}}`;
  });

  // Block math: \[...\]  (LaTeX document style — Gemini/GPT output)
  html = html.replace(/\\\[([\s\S]+?)\\\]/g, (match, math) => {
    const rendered = renderKatex(math.trim(), true);
    return `<div style="overflow-x: auto; margin: 12px 0; text-align: center;">${rendered}</div>`;
  });

  // Block math: $$...$$
  html = html.replace(/\$\$([\s\S]+?)\$\$/g, (match, math) => {
    const rendered = renderKatex(math.trim(), true);
    return `<div style="overflow-x: auto; margin: 12px 0; text-align: center;">${rendered}</div>`;
  });

  // Inline math: \(...\)  (LaTeX document style — Gemini/GPT output)
  html = html.replace(/\\\((.+?)\\\)/g, (match, math) => {
    return renderKatex(math.trim(), false);
  });

  // Inline math: $...$  (single dollar, not escaped)
  html = html.replace(/(?<!\$)\$(?!\$)([^$\n]+?)(?<!\$)\$(?!\$)/g, (match, math) => {
    return renderKatex(math.trim(), false);
  });
  
  // Convert headers (must be at start of line)
  html = html.replace(/^### (.*)$/gim, '<h3 style="margin: 12px 0 8px 0; font-weight: 600; font-size: 1.1em;">$1</h3>');
  html = html.replace(/^## (.*)$/gim, '<h2 style="margin: 16px 0 10px 0; font-weight: 600; font-size: 1.25em;">$1</h2>');
  html = html.replace(/^# (.*)$/gim, '<h1 style="margin: 20px 0 12px 0; font-weight: 700; font-size: 1.5em;">$1</h1>');
  
  // Convert bold (before italic to avoid conflicts)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // Convert italic (but not underscores used in math subscripts that are already converted)
  html = html.replace(/\*([^*]+?)\*/g, '<em>$1</em>');
  
  // Convert inline code
  html = html.replace(/`(.+?)`/g, '<code style="background: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 0.9em;">$1</code>');
  
  // Convert numbered lists (must be at start of line)
  html = html.replace(/^\d+\.\s+(.*)$/gim, '<li style="margin-left: 20px;">$1</li>');
  
  // Convert bullet points (must be at start of line)
  html = html.replace(/^[•\-\*]\s+(.*)$/gim, '<li style="margin-left: 20px;">$1</li>');
  
  // Wrap consecutive <li> tags in <ul> with proper spacing
  html = html.replace(/(<li[\s\S]*?<\/li>\s*)+/g, (match) => {
    return `<ul style="margin: 8px 0; padding-left: 0; list-style-position: inside;">${match}</ul>`;
  });
  
  // Convert links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #1976d2; text-decoration: underline;" target="_blank" rel="noopener noreferrer">$1</a>');

  // Convert line breaks to paragraphs (handle double newlines)
  const paragraphs = html.split(/\n\n+/);
  html = paragraphs.map(para => {
    para = para.trim();
    // Don't wrap if already a tag
    if (!para) return '';
    if (para.startsWith('<h') || para.startsWith('<ul') || para.startsWith('<ol') || para.startsWith('<li') || para.startsWith('<div') || para.startsWith('<svg') || para.startsWith('{{SVG_BLOCK_') || para.startsWith('{{CODE_BLOCK_')) {
      return para;
    }
    // Replace single newlines with <br> within paragraphs
    para = para.replace(/\n/g, '<br>');
    return `<p style="margin: 8px 0;">${para}</p>`;
  }).filter(p => p).join('');

  // Restore code blocks (with copy button)
  codeBlocks.forEach((block, idx) => {
    const code = block.slice(3, -3).replace(/^[a-z]*\n/, ''); // strip language tag + opening ```
    const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const copyBtn = `<button onclick="navigator.clipboard.writeText(this.closest('div').querySelector('code').innerText);this.textContent='Copied!';setTimeout(()=>this.textContent='Copy',1500)" style="position:absolute;top:8px;right:8px;padding:2px 10px;background:#3a3a3a;color:#ccc;border:1px solid #555;border-radius:4px;font-size:0.78em;font-family:monospace;cursor:pointer;line-height:1.6;">Copy</button>`;
    html = html.replace(
      `{{CODE_BLOCK_${idx}}}`,
      `<div style="position:relative;margin:10px 0;">${copyBtn}<pre style="background:#1e1e1e;color:#d4d4d4;padding:12px 16px;padding-right:64px;border-radius:6px;overflow-x:auto;font-family:monospace;font-size:0.88em;line-height:1.5;margin:0;"><code>${escaped}</code></pre></div>`
    );
  });

  // Restore SVG blocks (rendered as diagrams with a styled container)
  svgBlocks.forEach((svg, idx) => {
    html = html.replace(
      `{{SVG_BLOCK_${idx}}}`,
      `<div style="margin:12px 0;text-align:center;background:#fafafa;border:1px solid #e0e0e0;border-radius:8px;padding:12px;overflow-x:auto;">${svg}</div>`
    );
  });

  return html;
};

/**
 * Simpler version for chat messages (preserves formatting but less aggressive)
 */
export const formatChatMessage = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  let html = text;
  
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  
  // Italic  
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  
  // Line breaks
  html = html.replace(/\n/g, '<br>');
  
  return html;
};

